import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import {
  createExchangeRate,
  listExchangeRates,
  syncFxRates,
} from '../api/exchangeRates.js';
import { getFxCurrencies, type RequiredFxPair } from '../api/orgs.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate } from '../lib/format.js';
import { useAuth } from '../auth/AuthContext.js';
import type {
  CurrencyCode,
  ExchangeRateResponse,
  ExchangeRateSource,
} from '@kora/types';

/**
 * /fx-rates — FX rate administration for THIS organization.
 *
 * Each tenant owns its own rate table (post-migration). Different
 * tenants can legitimately set different rates for the same pair
 * (CBN for Nigerian orgs, BCC for DRC orgs, market aggregator for
 * multinationals). RLS on ExchangeRate + req.withScope enforce
 * isolation — an admin here only sees / writes rates for the org
 * tied to their JWT.
 *
 * Mutation model: append-only. Editing a rate means posting a new row
 * with a later `effectiveDate`; the snapshot builder picks the newest
 * rate ≤ payDate. Historical runs stay frozen on whatever was effective
 * when they were created.
 *
 * Today only the `manual` source is used; Sprint 2 adds automated
 * ingest (OpenExchangeRates, central-bank feeds). The UI is the same
 * either way — the `source` pill tells operators where each row came
 * from.
 */

export const exchangeRatesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/fx-rates',
  component: ExchangeRatesPage,
});

const RateForm = z
  .object({
    fromCurrency: z.enum(['NGN', 'GHS', 'CDF', 'USD']),
    toCurrency: z.enum(['NGN', 'GHS', 'CDF', 'USD']),
    rate: z
      .string()
      .regex(/^\d+(\.\d{1,8})?$/, 'Positive decimal, up to 8 fractional digits')
      .refine((v) => Number(v) > 0, 'Must be greater than zero'),
    effectiveDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    source: z.enum([
      'manual',
      'central_bank_ng',
      'central_bank_gh',
      'central_bank_cd',
      'openexchangerates',
      'oanda',
      'other',
    ]),
    note: z.string().max(500).optional(),
  })
  .refine((v) => v.fromCurrency !== v.toCurrency, {
    message: 'From and To must differ',
    path: ['toCurrency'],
  });
type RateForm = z.infer<typeof RateForm>;

function ExchangeRatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const fallbackBase = (user?.organization.baseCurrency ?? 'USD') as CurrencyCode;

  // Pull the dynamic allowed-currencies list from the server. The set
  // is {baseCurrency, USD, ...currencies of countries where the org
  // has active employees}. Mokoafrika hires an NG developer → NGN
  // appears in the dropdown without anyone changing settings.
  const { data: fx, isLoading: fxLoading } = useQuery({
    queryKey: ['fx-currencies'],
    queryFn: getFxCurrencies,
  });

  const baseCurrency = (fx?.baseCurrency ?? fallbackBase) as CurrencyCode;
  const allowedCurrencies = (fx?.currencies ?? [baseCurrency, 'USD']) as readonly CurrencyCode[];
  const employeeCountries = fx?.employeeCountries ?? [];

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['exchange-rates', 'list'],
    queryFn: () => listExchangeRates({ limit: 200 }),
  });

  const [formOpen, setFormOpen] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    tone: 'success' | 'error';
    text: string;
  } | null>(null);

  // The "no FX needed" state is rare: USD-base org, no foreign-country
  // employees. As soon as a non-USD employee is hired, currencies grow.
  const noFxNeeded = allowedCurrencies.length <= 1;

  // One-shot pull from the open-market FX provider. Fires only when the
  // operator clicks "Sync from market". Future: scheduled background
  // sync via BullMQ when we have the infra.
  const syncMutation = useMutation({
    mutationFn: syncFxRates,
    onSuccess: async (res) => {
      const headline =
        res.syncedPairs > 0
          ? `Synced ${res.syncedPairs} rate${res.syncedPairs === 1 ? '' : 's'}${
              res.provider ? ` from ${res.provider}` : ''
            }${res.effectiveDate ? ` effective ${res.effectiveDate}` : ''}.`
          : res.message;
      setSyncMessage({ tone: 'success', text: headline });
      await queryClient.invalidateQueries({ queryKey: ['exchange-rates', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['fx-currencies'] });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.status === 502
            ? 'Open-market FX provider is unreachable right now. Set rates manually, or try again in a minute.'
            : err.message
          : 'Sync failed. Try again.';
      setSyncMessage({ tone: 'error', text: message });
    },
  });

  return (
    <section className="p-8 space-y-6 max-w-5xl">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl">Exchange rates</h1>
          <p className="text-sm text-gray-400 max-w-2xl">
            Rates between currencies your team uses:{' '}
            <strong>{allowedCurrencies.join(', ')}</strong>. Used by every
            payroll calculation; each run snapshots the rate effective on its
            pay date. Append-only — to update, add a new rate with a later
            effective date.
          </p>
          {employeeCountries.length > 0 ? (
            <p className="text-xs text-gray-500 mt-1">
              Currency list driven by your team:{' '}
              <strong>{baseCurrency}</strong> (base) + USD (reporting pivot)
              {employeeCountries.length > 0
                ? ` + ${employeeCountries.join(', ')} employees`
                : ''}
              .
            </p>
          ) : null}
        </div>
        {!formOpen && !noFxNeeded ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                setSyncMessage(null);
                syncMutation.mutate();
              }}
              disabled={syncMutation.isPending}
              className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-50"
              title="Pull the latest open-market rates (Xe-style aggregator)"
            >
              {syncMutation.isPending ? 'Syncing…' : 'Sync from market'}
            </button>
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200"
            >
              Add rate
            </button>
          </div>
        ) : null}
      </header>

      {syncMessage ? (
        <div
          role="status"
          className={`rounded border p-3 text-sm ${
            syncMessage.tone === 'success'
              ? 'border-emerald/40 bg-emerald/10 text-emerald'
              : 'border-red-900 bg-red-950/40 text-red-300'
          }`}
        >
          {syncMessage.text}
        </div>
      ) : null}

      {noFxNeeded && !fxLoading ? (
        <div className="rounded border border-gray-800 bg-gray-950 p-6 text-sm text-gray-400">
          This organization's base currency is USD and there are no employees
          working in non-USD countries. No FX rates are required — payroll
          runs don't need to convert anything. Hire someone in NG, GH, or CD
          and currencies will appear here.
        </div>
      ) : null}

      {fx && fx.missingPairs && fx.missingPairs.length > 0 ? (
        <MissingFxPairsBanner
          missing={fx.missingPairs}
          onPickPair={() => setFormOpen(true)}
        />
      ) : null}

      {formOpen && !noFxNeeded ? (
        <AddRateForm
          allowedCurrencies={allowedCurrencies}
          baseCurrency={baseCurrency}
          onCancel={() => setFormOpen(false)}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['exchange-rates', 'list'] });
            await queryClient.invalidateQueries({ queryKey: ['fx-currencies'] });
            setFormOpen(false);
          }}
        />
      ) : null}

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}
      {data && data.rates.length === 0 && !noFxNeeded ? <EmptyState /> : null}
      {data && data.rates.length > 0 ? <RatesTable rates={data.rates} /> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Missing-rates banner — surfaces "you need to add these before payroll runs"
// ---------------------------------------------------------------------------
//
// Driven by the server's missingPairs computation (see
// apps/api/src/services/computeMissingFxPairs.ts). Each pair has one
// or more reasons attached — roll-up for the dashboard totals,
// spec-conversion for tax-basis calculation. Operators see exactly
// which pairs are missing and why, no sleuthing required.

function MissingFxPairsBanner({
  missing,
  onPickPair,
}: {
  missing: readonly RequiredFxPair[];
  onPickPair: () => void;
}): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded border border-yellow-900 bg-yellow-950/30 p-5 space-y-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-yellow-300">
            {missing.length === 1
              ? '1 FX rate missing'
              : `${missing.length} FX rates missing`}
          </h2>
          <p className="text-xs text-yellow-200/80 mt-1 max-w-2xl">
            Payroll runs will fail for the affected employees until these
            rates are set. The snapshot builder auto-inverts, so a single
            direction per pair is enough.
          </p>
        </div>
        <button
          type="button"
          onClick={onPickPair}
          className="rounded bg-yellow-300 text-yellow-950 px-3 py-1 text-xs font-medium hover:bg-yellow-200 shrink-0"
        >
          Add now
        </button>
      </header>
      <ul className="space-y-2">
        {missing.map((pair) => (
          <li
            key={`${pair.currencyA}|${pair.currencyB}`}
            className="rounded border border-yellow-900/60 bg-black/20 p-3 text-xs space-y-1"
          >
            <div className="font-mono text-yellow-200">
              {pair.currencyA} ↔ {pair.currencyB}
            </div>
            <ul className="text-gray-400 pl-3 space-y-0.5 list-disc list-inside">
              {pair.reasons.map((r, i) => (
                <li key={i}>{r.message}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RatesTable({ rates }: { rates: ExchangeRateResponse[] }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Pair</th>
            <th className="px-4 py-3 font-medium text-right">Rate</th>
            <th className="px-4 py-3 font-medium">Effective</th>
            <th className="px-4 py-3 font-medium">Source</th>
            <th className="px-4 py-3 font-medium">Added</th>
          </tr>
        </thead>
        <tbody>
          {rates.map((r) => (
            <tr key={r.id} className="border-t border-gray-900 hover:bg-gray-950">
              <td className="px-4 py-3">
                <span className="font-mono text-xs">
                  {r.fromCurrency} → {r.toCurrency}
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-xs text-right">
                1 {r.fromCurrency} = {trimRate(r.rate)} {r.toCurrency}
              </td>
              <td className="px-4 py-3 text-gray-400">{r.effectiveDate}</td>
              <td className="px-4 py-3 text-gray-400">
                <SourcePill source={r.source} />
              </td>
              <td className="px-4 py-3 text-gray-500 text-xs">
                {formatDate(r.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Drop trailing zeros from the 8-decimal rate for readability. */
function trimRate(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

function SourcePill({ source }: { source: string }): JSX.Element {
  const color = source.startsWith('central_bank_')
    ? 'bg-indigo/10 text-indigo border-indigo/40'
    : source === 'manual'
      ? 'bg-gray-900 text-gray-300 border-gray-800'
      : source.startsWith('openexchangerates') || source === 'oanda'
        ? 'bg-blue-950 text-blue-300 border-blue-900'
        : source.endsWith(':inverted')
          ? 'bg-yellow-950 text-yellow-400 border-yellow-900'
          : 'bg-gray-900 text-gray-400 border-gray-800';
  return <span className={`text-xs px-2 py-0.5 rounded border ${color}`}>{source}</span>;
}

// ---------------------------------------------------------------------------
// Add rate form
// ---------------------------------------------------------------------------

function AddRateForm(props: {
  /** All currencies the org needs FX for — includes base + USD + employee-country currencies. */
  allowedCurrencies: readonly CurrencyCode[];
  baseCurrency: CurrencyCode;
  onCancel: () => void;
  onSuccess: () => Promise<void>;
}): JSX.Element {
  const [serverError, setServerError] = useState<string | null>(null);

  // The form accepts any pair of distinct currencies in the allowed
  // set. Default to USD→base since most central-bank / aggregator
  // sources quote that direction (CBN, BCC, Bank of Ghana all say
  // "1 USD = X local"). Operators flip via the dropdown.
  const currencyOptions = props.allowedCurrencies.map((c) => ({
    value: c,
    label: `${c} — ${currencyLabel(c)}`,
  }));

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RateForm>({
    resolver: zodResolver(RateForm),
    defaultValues: {
      fromCurrency: 'USD',
      toCurrency: props.baseCurrency,
      rate: '',
      effectiveDate: new Date().toISOString().slice(0, 10),
      source: 'manual',
      note: '',
    },
  });

  const from = watch('fromCurrency');
  const to = watch('toCurrency');

  const mutation = useMutation({
    mutationFn: createExchangeRate,
    onSuccess: async () => {
      await props.onSuccess();
      setServerError(null);
    },
  });

  async function onSubmit(values: RateForm): Promise<void> {
    setServerError(null);
    try {
      await mutation.mutateAsync({
        fromCurrency: values.fromCurrency as CurrencyCode,
        toCurrency: values.toCurrency as CurrencyCode,
        rate: values.rate,
        effectiveDate: values.effectiveDate,
        source: values.source as ExchangeRateSource,
        ...(values.note ? { note: values.note } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError(
          'A rate for this currency pair + effective date already exists. To update, use a later effective date.',
        );
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Try again.');
      }
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded border border-gray-800 bg-gray-950 p-6 space-y-4"
      noValidate
    >
      <h2 className="font-serif text-xl">Add exchange rate</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FxSelectField
          id="fromCurrency"
          label="From *"
          error={errors.fromCurrency?.message}
          inputProps={register('fromCurrency')}
          options={currencyOptions}
        />
        <FxSelectField
          id="toCurrency"
          label="To *"
          error={errors.toCurrency?.message}
          inputProps={register('toCurrency')}
          options={currencyOptions}
        />
        <FxField
          id="rate"
          label={`Rate — 1 ${from} = ? ${to} *`}
          placeholder="1500"
          error={errors.rate?.message}
          inputProps={register('rate')}
        />
        <FxField
          id="effectiveDate"
          label="Effective from *"
          type="date"
          error={errors.effectiveDate?.message}
          inputProps={register('effectiveDate')}
        />
        <FxSelectField
          id="source"
          label="Source *"
          error={errors.source?.message}
          inputProps={register('source')}
          options={[
            { value: 'manual', label: 'Manual entry' },
            { value: 'central_bank_ng', label: 'CBN (Nigeria)' },
            { value: 'central_bank_gh', label: 'Bank of Ghana' },
            { value: 'central_bank_cd', label: 'BCC (DRC)' },
            { value: 'openexchangerates', label: 'OpenExchangeRates' },
            { value: 'oanda', label: 'OANDA' },
            { value: 'other', label: 'Other' },
          ]}
        />
        <div className="sm:col-span-3">
          <FxField
            id="note"
            label="Note (optional, persisted in audit log)"
            placeholder="E.g. CBN daily rate for 2026-04-24"
            error={errors.note?.message}
            inputProps={register('note')}
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Only one direction needed — the snapshot builder inverts automatically
        when the reverse pair is queried (1/rate, tagged&nbsp;
        <code className="font-mono">:inverted</code>&nbsp;in the source).
      </p>

      {serverError ? <Alert variant="error">{serverError}</Alert> : null}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {isSubmitting || mutation.isPending ? 'Saving...' : 'Add rate'}
        </button>
        <button
          type="button"
          onClick={props.onCancel}
          className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function currencyLabel(c: CurrencyCode): string {
  switch (c) {
    case 'NGN':
      return 'Nigerian Naira';
    case 'GHS':
      return 'Ghana Cedi';
    case 'CDF':
      return 'Congolese Franc';
    case 'USD':
      return 'US Dollar';
  }
}

// ---------------------------------------------------------------------------
// Form primitives
// ---------------------------------------------------------------------------

interface FxFieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<RateForm>>['register']>;
}

function FxField({ id, label, type = 'text', placeholder, error, inputProps }: FxFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

interface FxSelectFieldProps extends Omit<FxFieldProps, 'type' | 'placeholder'> {
  options: Array<{ value: string; label: string }>;
}

function FxSelectField({ id, label, error, inputProps, options }: FxSelectFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
        {...inputProps}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Non-happy states
// ---------------------------------------------------------------------------

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading rates...
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded border border-dashed border-gray-800 p-10 text-center space-y-2">
      <h2 className="font-medium text-white">No exchange rates set</h2>
      <p className="text-sm text-gray-400">
        Payroll runs that need currency conversion will fail until at least one
        rate is set. Add a rate to get started.
      </p>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }): JSX.Element {
  const message =
    error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Unknown error';
  return (
    <Alert
      variant="error"
      title="Could not load exchange rates"
      action={
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Retry
        </button>
      }
    >
      {message}
    </Alert>
  );
}
